"use client";

import React from "react";
import Link from "next/link";
import { trackFunnel } from "@/libs/analytics";
import HeroCrossfade from "./HeroCrossfade";
import {
  JTBD_AVATAR_HERO_IMAGES,
  JTBD_HEADSHOT_HERO_IMAGES,
  type JtbdHeroImageCrop,
} from "./jtbdHeroMedia";

function fireClick(
  tool_id: string,
  card_type: "hero",
  card_id: string,
  section: string,
  destination_href: string
) {
  trackFunnel("tool_engagement", "dashboard_card_clicked", {
    tool_id,
    card_type,
    card_id,
    section,
    destination_href,
  });
}

function HeroCard({
  href,
  title,
  subtitle,
  badge,
  ctaText,
  toolId,
  previewImages,
  imageCrop = "cover",
}: {
  href: string;
  title: string;
  subtitle: string;
  badge?: string;
  ctaText: string;
  toolId: string;
  previewImages: string[];
  imageCrop?: JtbdHeroImageCrop;
}) {
  return (
    <Link
      href={href}
      onClick={() => fireClick(toolId, "hero", toolId, "hero", href)}
      className="group relative block w-full rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-all duration-300"
    >
      <div className="relative w-full aspect-[16/10] bg-gray-100 overflow-hidden">
        <HeroCrossfade images={previewImages} alt={title} imageCrop={imageCrop} priority />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {badge && (
          <div className="absolute top-3 right-3 z-10 bg-[#f16142] backdrop-blur-sm rounded-full px-2.5 py-0 pb-1 shadow-lg">
            <span className="text-[11px] font-bold text-white tracking-wide uppercase">{badge}</span>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
          <h3 className="text-white text-2xl md:text-3xl font-bold mb-2">{title}</h3>
          <p className="text-white/80 text-sm md:text-base mb-4">{subtitle}</p>
          <span className="inline-flex items-center justify-center bg-primary text-white rounded-xl px-5 py-3 font-semibold hover:opacity-90 transition text-sm leading-none">
            {ctaText}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function JtbdHero() {
  return (
    <div>
      <div className="mb-8">
        <h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900"
          style={{ fontFamily: "var(--font-merriweather)" }}
        >
          What are you creating today?
        </h1>
        <p className="text-gray-500 text-base md:text-lg mt-2">
          Professional headshots, creative avatars, and style tools - all from one selfie.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <HeroCard
          href="/editor-v2/ai-headshots"
          title="Studio-quality AI Headshots"
          subtitle="515+ outfits, backgrounds & poses from one selfie"
          badge="Bestselling"
          ctaText="Create Headshots →"
          toolId="ai-headshots"
          previewImages={JTBD_HEADSHOT_HERO_IMAGES}
          imageCrop="fill-width"
        />
        <HeroCard
          href="/editor-v2/ai-avatars"
          title="Creative Profile Pictures"
          subtitle="197 styles - lifestyle, viral, fantasy & more"
          badge="Hot"
          ctaText="Create Avatars →"
          toolId="ai-avatars"
          previewImages={JTBD_AVATAR_HERO_IMAGES}
        />
      </div>
    </div>
  );
}
